import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Mail, Lock, LogIn, UserPlus, ArrowLeft, Eye, EyeOff } from 'lucide-react';
import { supabase } from '../lib/supabase';

const AuthPage = () => {
  const [isLogin, setIsLogin] = useState(true);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isAdult, setIsAdult] = useState(false);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    // Vérification pour l'inscription
    if (!isLogin) {
      if (!isAdult) {
        toast.error("Vous devez avoir 18 ans ou plus pour créer un compte.");
        setLoading(false);
        return;
      }
    }

    try {
      // The Supabase SDK owns the session: persistence in localStorage and
      // automatic refresh token rotation are handled internally.
      const { error } = isLogin
        ? await supabase.auth.signInWithPassword({ email, password })
        : await supabase.auth.signUp({ email, password });

      if (error) {
        const errorCode = error.code || '';
        const errorMsg = error.message || '';

        if (errorCode === 'invalid_credentials' || errorMsg.includes('Invalid login')) {
          toast.error('Email ou mot de passe incorrect');
        } else if (errorMsg.includes('already registered') || errorCode === 'user_already_exists') {
          toast.error('Cet email est déjà utilisé');
        } else if (errorMsg.includes('Email not confirmed') || errorCode === 'email_not_confirmed') {
          toast.error('Veuillez confirmer votre email');
        } else if (errorCode === 'email_address_invalid') {
          toast.error('Adresse email invalide');
        } else {
          toast.error(errorMsg || 'Erreur de connexion');
        }
        setLoading(false);
        return;
      }

      if (isLogin) {
        toast.success('Connexion réussie !');
        window.location.href = '/';
      } else {
        toast.success('Compte créé ! Vérifiez votre email pour confirmer.');
        setIsLogin(true);
        setLoading(false);
      }
    } catch (error) {
      console.error('Auth error:', error);
      toast.error(error.message || 'Erreur réseau. Vérifiez votre connexion.');
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-amber-50 to-white flex items-center justify-center px-4">
      <div className="w-full max-w-md">
        <button
          onClick={() => navigate('/')}
          className="flex items-center gap-2 text-amber-800 hover:text-amber-900 mb-6"
        >
          <ArrowLeft size={20} />
          Retour
        </button>

        <div className="bg-white rounded-2xl shadow-xl p-8 border border-amber-100">
          <div className="text-center mb-8">
            <h1 className="text-2xl font-serif text-amber-900 mb-2">
              {isLogin ? 'Connexion' : 'Créer un compte'}
            </h1>
            <p className="text-amber-700 text-sm">
              {isLogin 
                ? 'Accédez à vos garde-pages et surlignages' 
                : 'Rejoignez la communauté Bible Fillion'}
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <label className="block text-sm font-medium text-amber-800 mb-2">
                Email
              </label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 text-amber-400" size={20} />
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="votre@email.com"
                  required
                  className="w-full pl-11 pr-4 py-3 border border-amber-200 rounded-xl focus:ring-2 focus:ring-amber-500 focus:border-transparent outline-none transition"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-amber-800 mb-2">
                Mot de passe
              </label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 text-amber-400" size={20} />
                <input
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  required
                  minLength={6}
                  className="w-full pl-11 pr-12 py-3 border border-amber-200 rounded-xl focus:ring-2 focus:ring-amber-500 focus:border-transparent outline-none transition"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-amber-400 hover:text-amber-600 transition"
                  aria-label={showPassword ? "Masquer le mot de passe" : "Afficher le mot de passe"}
                >
                  {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                </button>
              </div>
              {!isLogin && (
                <p className="text-xs text-amber-600 mt-1">Minimum 6 caractères</p>
              )}
            </div>

            {/* Confirmation d'âge - Uniquement pour l'inscription */}
            {!isLogin && (
              <div>
                <label className="flex items-start gap-3 cursor-pointer group">
                  <input
                    type="checkbox"
                    checked={isAdult}
                    onChange={(e) => setIsAdult(e.target.checked)}
                    required
                    className="mt-0.5 h-4 w-4 rounded border-amber-300 text-amber-800 focus:ring-amber-500 cursor-pointer"
                  />
                  <span className="text-sm text-amber-800">
                    J'ai <strong>18 ans ou plus</strong> <span className="text-red-600">*</span>
                  </span>
                </label>
              </div>
            )}

            <button
              type="submit"
              disabled={loading}
              className="w-full bg-amber-800 hover:bg-amber-900 text-white py-3 rounded-xl font-medium flex items-center justify-center gap-2 transition disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? (
                <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
              ) : isLogin ? (
                <>
                  <LogIn size={20} />
                  Se connecter
                </>
              ) : (
                <>
                  <UserPlus size={20} />
                  Créer mon compte
                </>
              )}
            </button>
          </form>

          <div className="mt-6 text-center">
            <button
              onClick={() => setIsLogin(!isLogin)}
              className="text-amber-700 hover:text-amber-900 text-sm"
            >
              {isLogin 
                ? "Pas encore de compte ? S'inscrire" 
                : 'Déjà un compte ? Se connecter'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AuthPage;
